import React, { FC, useEffect, useMemo } from "react";
import { Checkbox } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { Notice } from "../../CopyResource/components/Project/styled";
import { ConfirmText, Content } from "../styled";
import { Loading } from "./Loading";

import { layerInfosWithDeps } from "../../../../resources/layers";
import { useResourcesLimits } from "../../CopyResource/hooks/useResourcesLimits";
import { useSelectedResourcesNames } from "../../CopyResource/hooks/useSelectedResourcesNames";

import { BulkCopyOptionsProps } from "../../CopyResource/types";

type Props = Omit<BulkCopyOptionsProps, "layers" | "toggleLayers">;

export const BulkCopyLayersContent: FC<Props> = ({ copyTables, toggleTables, onLoading, limits }) => {
  const { t } = useTranslation("dialogs");
  const { setTablesTotalCount, isInitialTablesLimit, isTablesLimitExceeded } = useResourcesLimits(limits);
  const layersToCopy: string[] = useSelectedResourcesNames();
  const { data: info, loading } = layerInfosWithDeps.use({ input: layersToCopy });
  const tablesCount: number = (info && info.deps && info.deps.tables && info.deps.tables.length) || 0;
  const isAnyDep: boolean = tablesCount > 0;

  const confirmText = useMemo(
    () => (isAnyDep ? t("bulkCopy.copyLayersConfirmText1") : t("bulkCopy.copyLayersConfirmText2")),
    [isAnyDep, t],
  );

  useEffect(() => {
    isInitialTablesLimit && copyTables && toggleTables();
  }, [isInitialTablesLimit]);

  useEffect(() => {
    if (limits) {
      setTablesTotalCount(limits.currentTableCount + (copyTables ? tablesCount : 0));
    }
  }, [limits, copyTables, tablesCount]);

  useEffect(() => onLoading(loading), [onLoading, loading]);

  return (
    <Content>
      {loading ? (
        <Loading />
      ) : (
        <>
          {!isInitialTablesLimit ? (
            <ConfirmText>{confirmText}</ConfirmText>
          ) : (
            <Notice info style={{ marginTop: "1rem" }}>
              {t("bulkCopy.tablesLimitReached")} ({limits.currentTableCount} {t("bulkCopy.of")}{" "}
              {limits && limits.maxTablesCount}
              ). {t("bulkCopy.newLayersWillUseOriginalData")}
            </Notice>
          )}
          {!isInitialTablesLimit ? (
            <>
              {isAnyDep ? (
                <Checkbox
                  checked={copyTables}
                  label={`${t("bulkCopy.copyTables")} (${tablesCount})`}
                  onChange={toggleTables}
                />
              ) : null}
              {limits && !isInitialTablesLimit && isTablesLimitExceeded ? (
                <Notice warning style={{ marginTop: "1rem" }}>
                  {t("bulkCopy.youHaveGot")}{" "}
                  {isTablesLimitExceeded
                    ? `${limits.currentTableCount} ${t("bulkCopy.of")} ${limits.maxTablesCount} ${t(
                        "bulkCopy.ofTables",
                      )}`
                    : ""}
                  , {t("bulkCopy.notAllTablesWillBeCopied")}
                </Notice>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </Content>
  );
};
